/* The rule editor's row model: draft rows, their completeness, and the document the API takes.
 *
 * Bookkeeping only — add a row, edit a field, and turn the rows into the document the authoring route
 * takes. What the rows *mean* together (deny wins, narrowing, shadowing) is `workspace.ts`'s, so the
 * editor, the summary and the request checker read rules one way.
 */

import type {
  PolicyDocument,
  PolicyProblem,
  PolicyRule,
  PolicyVocabulary,
} from '@/shared/api/generated/policy';

/** A row being edited. **Every field is optional, unlike `PolicyRule`.**
 *
 * A half-filled row is the normal state of a form somebody is using, and modelling it with the
 * wire type would force a placeholder — an empty-string role, or `effect: 'allow'` chosen by the
 * code rather than the person. Both are worse than absence: a placeholder role would be sent to the
 * server as a real value, and a defaulted effect would mean the most consequential field on the row
 * was filled in by nobody.
 *
 * `id` is local and never sent. It exists so React can key a row that has no distinguishing content
 * yet — two blank rows are otherwise identical, and keying by index makes a removal re-render the
 * wrong row's contents.
 */
export interface DraftRule {
  readonly id: string;
  readonly role: string | null;
  readonly resourceType: string | null;
  readonly action: string | null;
  readonly resourceId: string | null;
  readonly effect: 'allow' | 'deny' | null;
}

/** `resourceId` is deliberately not in this list.
 *
 * Null there is a *choice* — "every resource of this type" — rather than an unanswered question, so
 * a row without one is complete. That is the one field where absence carries meaning, which is why
 * completeness cannot be a simple "no nulls" check.
 */
const REQUIRED: readonly (keyof DraftRule)[] = [
  'role',
  'resourceType',
  'action',
  'effect',
];

let counter = 0;

/** A blank row. Sequential ids rather than random ones so a test can assert on them.
 *
 * **Allow by default, as the design draws it.** A rule is how a policy says what a role *may* do —
 * the module permits only what an allow matches — so allow is what a new row is for, and a deny is
 * the deliberate exception somebody switches to. The role, resource and action still start empty,
 * so a row cannot be saved until all three are chosen.
 */
export function blankRule(): DraftRule {
  counter += 1;
  return {
    id: `rule-${String(counter)}`,
    role: null,
    resourceType: null,
    action: null,
    resourceId: null,
    effect: 'allow',
  };
}

/** Reopens saved rules for editing. */
export function draftFrom(rules: readonly PolicyRule[]): readonly DraftRule[] {
  return rules.map((rule) => {
    counter += 1;
    return {
      id: `rule-${String(counter)}`,
      role: rule.role,
      resourceType: rule.resource_type,
      action: rule.action,
      resourceId: rule.resource_id,
      effect: rule.effect === 'deny' ? ('deny' as const) : ('allow' as const),
    };
  });
}

/** Whether a row has everything the API needs. */
export function isComplete(rule: DraftRule): boolean {
  return REQUIRED.every((field) => {
    const value = rule[field];
    return typeof value === 'string' && value.length > 0;
  });
}

/** The rows that are ready, as the document the API takes. Incomplete rows are left out.
 *
 * **Silently dropping an incomplete row would be wrong**, which is why the editor refuses to save
 * while any row is incomplete rather than letting this quietly send fewer rules than the form shows.
 * This function is the translation; the refusal is the editor's.
 */
export function documentFrom(rules: readonly DraftRule[]): PolicyDocument {
  return {
    rules: rules.filter(isComplete).map((rule) => ({
      role: rule.role ?? '',
      action: rule.action ?? '',
      resource_type: rule.resourceType ?? '',
      resource_id: rule.resourceId,
      effect: rule.effect === 'deny' ? 'deny' : 'allow',
    })),
  };
}

/** Which actions a resource type has, or none when the type is unknown or unchosen.
 *
 * Read from the served vocabulary rather than a local table, so the Action dropdown cannot offer
 * something no request carries — a rule naming one would compile and match nothing.
 */
export function actionsFor(
  vocabulary: PolicyVocabulary | undefined,
  resourceType: string | null,
): readonly string[] {
  if (vocabulary === undefined || resourceType === null) return [];
  return (
    vocabulary.resources.find((each) => each.resource_type === resourceType)?.actions ??
    []
  );
}

/** Applies an edit, clearing what the edit invalidates.
 *
 * **Changing the resource type clears the action**, and that is the one piece of behaviour here
 * worth defending. Actions are per resource type — `catalog.dataset` has `read` and `profile`,
 * `rules.rule` has others — so a row left holding `profile` after a switch to `rules.rule` would be
 * a rule the server refuses, and worse, one the form shows as filled in. Clearing the resource id
 * too, because an id belongs to a type.
 */
export function withEdit(
  rule: DraftRule,
  field: 'role' | 'resourceType' | 'action' | 'resourceId' | 'effect',
  value: string | null,
): DraftRule {
  const chosen = value === null || value === '' ? null : value;
  if (field === 'resourceType') {
    return { ...rule, resourceType: chosen, action: null, resourceId: null };
  }
  if (field === 'effect') {
    return {
      ...rule,
      effect: chosen === 'deny' ? 'deny' : chosen === 'allow' ? 'allow' : null,
    };
  }
  return { ...rule, [field]: chosen };
}

/** Problems that belong to no row — the policy as a whole, or the platform's own generator. */
export function policyLevelProblems(
  problems: readonly PolicyProblem[],
): readonly PolicyProblem[] {
  return problems.filter((each) => each.rule_number === null);
}
